import * as Sentry from '@sentry/vue'
import { createHead } from '@vueuse/head'
import { createApp } from 'vue'
import i18nPlugin from '@/compositions/i18nPlugin'
import { redactBreadcrumb, redactSentryEvent } from '@/compositions/sentryRedact'
import routerInitPlugin from '@/router/routerInitPlugin'
import App from './App.vue'
import axiosPlugin from './plugins/axiosPlugin'
import elPlusPlugin from './plugins/elPlusPlugin'
import globalComponents from './plugins/globalComponents'
import piniaPlugin from './plugins/piniaPlugin'
import utilsPlugin from './plugins/utilsPlugin'

import './css/index.css'

// Migrate persisted data from vuex-persist (VUEX_PERSIST) to pinia-plugin-persistedstate (storeGame)
const _vuexPersistedRaw = localStorage.getItem('VUEX_PERSIST')
if (_vuexPersistedRaw) {
  try {
    const _vuexParsed = JSON.parse(_vuexPersistedRaw)
    if (_vuexParsed?.game) {
      localStorage.setItem('storeGame', JSON.stringify(_vuexParsed.game))
    }
  } catch {}
  localStorage.removeItem('VUEX_PERSIST')
}

const head = createHead()
const Vue = createApp(App)
Vue.use(routerInitPlugin)
// const router = routerInitPlugin.getVueRouter()
if (!import.meta.env.SSR
  && window?.location?.hostname
  && window?.location?.hostname !== 'localhost') {
  const isPWA = gaUtils.getLaunchMode() === 'pwa' // same detection as GA gs_launch_mode (incl. iOS navigator.standalone)
  Sentry.init({
    Vue,
    dsn: 'https://ff3dc9e85dcdf37c6cb5d313b6c8d709@o48349.ingest.us.sentry.io/4508160978649088',
    beforeSend(event, hint) {
      // An exception WITH frames but where none of them have a resolvable filename is the
      // signature of a cross-origin/injected third-party script — the browser deliberately
      // hides the real file/line for security reasons. (Exceptions with NO frames at all,
      // e.g. "Failed to fetch dynamically imported module", are a different, first-party
      // issue and must NOT be swept in here.) Fold matches into one stable issue instead of
      // letting minified message text — which changes across builds — split them into new,
      // unignored issues every time.
      const exceptionValue = event.exception?.values?.[0]
      const frames = exceptionValue?.stacktrace?.frames
      const hasResolvedFrame = frames?.some(frame => frame.filename && frame.filename !== 'undefined')
      const isExternalScriptError = Boolean(exceptionValue) && Boolean(frames?.length) && !hasResolvedFrame

      // A SyntaxError whose sole frame points at a same-origin URL that isn't a real JS
      // file — the signature of a broken/empty script src re-fetching the current HTML
      // page and failing to parse it as JS (see the window 'error' fallback in this file).
      // Same-origin is required here (opposite of externalScriptError above): an empty/
      // undefined src always resolves relative to the current document, so it can only ever
      // land back on our own domain — a cross-origin match would mean something unrelated.
      // As of writing, no first-party code sets a script src from a variable (only hardcoded
      // absolute URLs in FbCredentials.vue/GoogleCredentials.vue) — if that ever changes,
      // revisit this filter, since a real bug there would get folded into this same bucket.
      // Folded into one stable bucket rather than letting it fragment into per-page issues.
      const firstFrameFilename = frames?.[0]?.filename
      const isStaleAssetSyntaxError = exceptionValue?.type === 'SyntaxError'
        && /unexpected end of (?:input|script)/i.test(exceptionValue?.value || '')
        && Boolean(firstFrameFilename)
        && !/\.m?js$/i.test(firstFrameFilename || '')
        && (() => {
          try {
            return new URL(firstFrameFilename!, window.location.href).origin === window.location.origin
          } catch {
            return false
          }
        })()

      event.tags = {
        ...event.tags,
        isPWA: isPWA ? 'yes' : 'no',
        externalScriptError: isExternalScriptError ? 'yes' : 'no',
        staleAssetSyntaxError: isStaleAssetSyntaxError ? 'yes' : 'no'
      }

      if (isExternalScriptError) {
        event.fingerprint = ['external-injected-script-error']
        event.extra = {
          ...event.extra,
          rawUserAgent: navigator.userAgent,
          referrer: document.referrer,
          locationSearch: window.location.search,
          loadedScripts: Array.from(document.scripts).map(s => s.src).filter(Boolean)
        }
      }

      if (isStaleAssetSyntaxError) {
        event.fingerprint = ['stale-asset-syntax-error']
        event.extra = {
          ...event.extra,
          rawUserAgent: navigator.userAgent,
          referrer: document.referrer
        }
      }

      // A short, letter-only exception value (e.g. "ga", "Ba", "Aa") with either no frames
      // at all, or frames that only ever point back at the current page's own document
      // (not a real .js/.mjs file) — the signature of an injected/inline third-party script
      // whose minified variable names change across its own (unrelated) deploys. Distinct
      // from isExternalScriptError above (which requires a frame with an UNRESOLVED filename)
      // and isStaleAssetSyntaxError (which requires a SyntaxError specifically). Wrapped in
      // its own try/catch so a mistake here can never prevent the event from being sent.
      try {
        const isPageDocumentFrame = (filename?: string) => {
          if (!filename)
            return false
          try {
            const url = new URL(filename, window.location.href)
            return url.origin === window.location.origin && !/\.m?js$/i.test(url.pathname)
          } catch {
            return false
          }
        }
        // Some variants of this family append a leaked template-literal artifact after the
        // short minified name (e.g. "Ka`prod", "Ba`prod") — an optional backtick + word
        // suffix, distinct across builds like the prefix itself. Captured as its own group so
        // it can be tagged separately below without re-deriving it.
        const crypticValueMatch = (exceptionValue?.value || '').trim().match(/^[a-z]{1,4}(?:`([a-z]{1,10}))?$/i)
        const isCrypticThirdPartyError = !isExternalScriptError
          && !isStaleAssetSyntaxError
          && Boolean(exceptionValue)
          && Boolean(crypticValueMatch)
          && (!frames?.length || frames.every(frame => isPageDocumentFrame(frame.filename)))

        event.tags = {
          ...event.tags,
          crypticThirdPartyError: isCrypticThirdPartyError ? 'yes' : 'no',
          ...(isCrypticThirdPartyError && crypticValueMatch?.[1] && { crypticThirdPartySuffix: crypticValueMatch[1] })
        }

        if (isCrypticThirdPartyError) {
          event.fingerprint = ['cryptic-thirdparty-injected-error']
          const originalException = hint?.originalException
          // When a frame is blamed on the page document itself (isPageDocumentFrame above),
          // its line/col refer to the position inside an inline <script> tag's own text, per
          // the window.onerror spec — not the bundled app JS. Inventorying every inline
          // script's length/snippet lets us match that line number back to a specific script
          // (e.g. a GTM/CMP/native-WebView inline tag) without needing its actual source.
          const inlineScriptInventory = (() => {
            try {
              return Array.from(document.querySelectorAll('script:not([src])'))
                .map((s) => {
                  const text = s.textContent || ''
                  return {
                    lines: text.split('\n').length,
                    length: text.length,
                    snippet: text.slice(0, 60).replace(/\s+/g, ' ').trim()
                  }
                })
                .filter(entry => entry.length > 0)
            } catch {
              return []
            }
          })()
          event.extra = {
            ...event.extra,
            rawUserAgent: navigator.userAgent,
            referrer: document.referrer,
            locationSearch: window.location.search,
            loadedScripts: Array.from(document.scripts).map(s => s.src).filter(Boolean),
            scriptResourceEntries: performance.getEntriesByType?.('resource')
              ?.filter((entry: any) => entry.initiatorType === 'script')
              ?.map((entry: any) => entry.name)
              ?.slice(0, 50) ?? [],
            inlineScriptInventory,
            // Ties this error back to the gtag script-load diagnostics (source: gtagScriptError
            // in routerInitPlugin.ts): if gtag never loaded, window.gtag/dataLayer won't be in
            // the shape vue-gtag leaves them in, which would support "script failed to load,
            // then unrelated code assumed it existed" as this family's root cause.
            gtagTypeofGlobal: typeof (window as any).gtag,
            dataLayerLength: Array.isArray((window as any).dataLayer) ? (window as any).dataLayer.length : null,
            online: navigator.onLine,
            originalExceptionTypeofValue: typeof originalException,
            originalExceptionRaw: (() => {
              try {
                return typeof originalException === 'object' && originalException !== null
                  ? JSON.stringify(originalException)?.slice(0, 500)
                  : String(originalException).slice(0, 500)
              } catch {
                return 'unserializable'
              }
            })()
          }
        }
      } catch {}

      // single exit point - redacted last, so the extras added above are covered too
      return redactSentryEvent(event)
    },
    // breadcrumbs are redacted when recorded (and again in beforeSend): xhr/fetch URLs, navigation from/to, console args
    beforeBreadcrumb: breadcrumb => redactBreadcrumb(breadcrumb),
    // SDK v11 collects user info, cookies, headers, bodies, query params and frame variables by default -
    // keep the restrictive v10 behaviour (no PII; tokens live in query params and request bodies).
    // Only User-Agent is kept: Sentry derives browser/os/device from it (Referer is not sent - it can carry a URL with tokens)
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpHeaders: { request: { allow: ['User-Agent'] }, response: false },
      httpBodies: [],
      urlQueryParams: false,
      stackFrameVariables: false
    },
    integrations: [
      // Sentry.browserTracingIntegration({ router }),
      // Sentry.browserTracingIntegration
    ],
    environment: import.meta.env.VITE_PROD === 'on' ? 'production' : 'development',
    // Tracing
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    // tracePropagationTargets: ['localhost', /^https:\/\/yourserver\.io\/api/],
    // Session Replay — disabled
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0
  })
}

Vue.use(head)
Vue.use(axiosPlugin)
Vue.use(elPlusPlugin)
Vue.use(i18nPlugin)
Vue.use(utilsPlugin)
Vue.use(globalComponents)
Vue.use(piniaPlugin)
Vue.mount('#app')

let hasScheduledStaleAssetReload = false

// If sessionStorage isn't reliably available (strict privacy modes, storage disabled), we have
// no safe way to remember "already tried reloading" across a page reload — blindly reloading
// anyway would risk an infinite loop if the failure isn't just a one-off stale chunk. Detected
// once via a real write+read (not assumed), so we can skip the reload entirely in that case.
function isSessionStorageAvailable() {
  try {
    const testKey = '__vitePreloadStorageTest'
    sessionStorage.setItem(testKey, '1')
    sessionStorage.removeItem(testKey)
    return true
  } catch {
    return false
  }
}

// Shared recovery path for both vite:preloadError below (a stale chunk hash 404 after a
// deploy, reported as a rejected import() promise) and the window 'error' fallback further
// down (same symptom — a stale/broken asset — but some browsers don't reject the promise
// cleanly and instead throw a synchronous parse error caught by onerror instead).
function attemptStaleAssetRecovery(errorForSentry: unknown, recurredMsg: string, unavailableMsg: string) {
  if (hasScheduledStaleAssetReload)
    return // another stale-asset failure is already being handled — don't pile on reloads
  hasScheduledStaleAssetReload = true

  // Stable grouping: the captured error's message/frames come from the bundler's preload helper and
  // change with every Vite/Rolldown upgrade (asset URL form, placeholder, function name), which split
  // GAMESTURE-STORE-9G into a new issue (BS). Group by the recovery outcome instead.
  const captureRecoveryFailure = (msg: string) =>
    Sentry.captureException(errorForSentry, { extra: { msg }, fingerprint: ['stale-asset-recovery', msg] })

  if (!isSessionStorageAvailable()) {
    captureRecoveryFailure(unavailableMsg)
    return
  }

  const lastReload = Number(sessionStorage.getItem('vitePreloadReloadedAt') || 0)
  if (Date.now() - lastReload < 10000) {
    // Reload already happened recently (this is a fresh page load after it) and the
    // failure recurred — reloading again would loop. Nothing actionable to tell the user,
    // but log it for visibility since recovery not working is worth knowing about.
    captureRecoveryFailure(recurredMsg)
    return
  }

  sessionStorage.setItem('vitePreloadReloadedAt', String(Date.now()))
  setTimeout(() => window.location.reload(), 1000)
}

if (!import.meta.env.SSR) {
  // Vite dispatches this when a lazy-loaded chunk 404s — typically because the user
  // had the site open across a deploy and the old build's chunk hash no longer exists.
  // Reload once to pick up the current build instead of leaving the user on a broken page.
  window.addEventListener('vite:preloadError', (event) => {
    event.preventDefault()
    attemptStaleAssetRecovery(
      (event as any).payload,
      'vite:preloadError recurred after a recovery reload',
      'vite:preloadError — sessionStorage unavailable, skipped reload to avoid a loop risk'
    )
  })

  // Fallback for browsers that don't cleanly reject the dynamic import() promise when a
  // script URL resolves to something that isn't valid JS (e.g. an empty/broken src that
  // ends up re-fetching the current HTML page) — instead they throw a synchronous parse
  // error caught here, never reaching the vite:preloadError handler above. Scoped tightly
  // (SyntaxError + this exact message shape + a same-origin, non-.js filename) so we don't
  // swallow unrelated first-party syntax errors.
  const STALE_ASSET_SYNTAX_ERROR_RE = /unexpected end of (?:input|script)/i
  window.addEventListener('error', (event) => {
    if (!(event.error instanceof SyntaxError) || !STALE_ASSET_SYNTAX_ERROR_RE.test(event.message || ''))
      return
    let isSameOriginNonJs = false
    try {
      isSameOriginNonJs = new URL(event.filename, window.location.href).origin === window.location.origin
        && !/\.m?js$/i.test(event.filename || '')
    } catch {}
    if (!isSameOriginNonJs)
      return
    event.preventDefault()
    attemptStaleAssetRecovery(
      event.error,
      'stale-asset SyntaxError recurred after a recovery reload',
      'stale-asset SyntaxError — sessionStorage unavailable, skipped reload to avoid a loop risk'
    )
  })
}
